import { AnimatePresence, motion } from "framer-motion";
import { Download } from "lucide-react";
import { useState } from "react";
import { Button } from "src/components/ui/button";
import { useLanguage } from "../../../../../context/LanguageContext";
import DrawerAddDocuments from "./DrawerAddDocuments";
import DialogDeleteDocument from "./DialogDeleteDocument";
import DrawerEditDocument from "./DrawerEditDocument";
import { SubLessonData } from "../../../../../lib/languages";
import { UrlImage } from "../../../../../lib/api";
import { useAuth } from "../../../../../context/AuthContext";

export default function DocumentsTab({ documents, fetchDocuments }) {
  const { user } = useAuth();
  const isTeacher = user?.role === "teacher";

  const { language } = useLanguage();
  const t = SubLessonData[language] || SubLessonData["en"];
  const [hoveredCard, setHoveredCard] = useState(null);

  return (
    <div className="grid gap-4">
      {documents && documents.length > 0 ? (
        documents.map((docs) => (
          <div
            key={docs.id}
            onMouseEnter={() => setHoveredCard(docs.id)}
            onMouseLeave={() => setHoveredCard(null)}
          >
            <div className="flex items-center gap-2 relative">
              <span className="font-semibold text-xl flex-1">
                {docs?.title[language] || docs?.title.en || docs?.title.ar}
              </span>

              {/* Action buttons that appear on hover */}
              {isTeacher && (
                <AnimatePresence>
                  {hoveredCard === docs.id && (
                    <>
                      {/* Edit button */}
                      <motion.div
                        initial={{ opacity: 0, x: -10 }}
                        animate={{ opacity: 1, x: 0 }}
                        exit={{ opacity: 0, x: -10 }}
                        transition={{ duration: 0.2 }}
                      >
                        <div className="flex items-center">
                          <DrawerEditDocument
                            fetchDocuments={fetchDocuments}
                            item={docs}
                          />
                        </div>
                      </motion.div>

                      {/* Delete button */}
                      <motion.div
                        initial={{ opacity: 0, x: -10 }}
                        animate={{ opacity: 1, x: 0 }}
                        exit={{ opacity: 0, x: -10 }}
                        transition={{ duration: 0.2, delay: 0.05 }}
                      >
                        <div className="flex items-center">
                          <DialogDeleteDocument
                            item={docs}
                            fetchDocuments={fetchDocuments}
                          />
                        </div>
                      </motion.div>
                    </>
                  )}
                </AnimatePresence>
              )}
            </div>
            {docs?.file &&
              docs.file.map((file, index) => (
                <div
                  key={index}
                  className="flex items-center justify-between py-4 border-b px-5"
                >
                  <div className="flex items-center gap-3">
                    {FileSvg}
                    <span className="font-medium">
                      {docs.title[language] || docs.title.en || docs.title.ar}{" "}
                      {index + 1}
                    </span>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-primary"
                    asChild
                  >
                    <a
                      href={`${UrlImage}${file}`}
                      download="myfile.pdf"
                      target="_blank"
                    >
                      <Download className="h-4 w-4 ml-1 text-blue-600" />{" "}
                      {t.download || "Download"}
                    </a>
                  </Button>
                </div>
              ))}
          </div>
        ))
      ) : (
        <div className="p-4 text-center text-gray-500">{t.noDocuments}</div>
      )}
      {isTeacher && <DrawerAddDocuments fetchDocuments={fetchDocuments} />}
    </div>
  );
}

const FileSvg = (
  <svg
    width="24"
    height="31"
    viewBox="0 0 24 31"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M4.07157 0.5C1.95345 0.5 0.214844 2.2386 0.214844 4.35672V26.6433C0.214844 28.7614 1.95345 30.5 4.07157 30.5H19.9296C22.0477 30.5 23.7863 28.7614 23.7863 26.6433V9.07143H18.4291C16.667 9.07143 15.2148 7.61932 15.2148 5.85714V0.5H4.07157ZM17.3577 0.661133V5.85714C17.3577 6.46924 17.817 6.92857 18.4291 6.92857H23.6251C23.4514 6.36196 23.1414 5.84003 22.7148 5.4135L18.8728 1.57143C18.4462 1.14491 17.9243 0.834887 17.3577 0.661133ZM6.64342 12.2857H17.3577C17.6419 12.2857 17.9144 12.3986 18.1153 12.5995C18.3162 12.8005 18.4291 13.073 18.4291 13.3571C18.4291 13.6413 18.3162 13.9138 18.1153 14.1148C17.9144 14.3157 17.6419 14.4286 17.3577 14.4286H6.64342C6.35926 14.4286 6.08673 14.3157 5.8858 14.1148C5.68487 13.9138 5.57199 13.6413 5.57199 13.3571C5.57199 13.073 5.68487 12.8005 5.8858 12.5995C6.08673 12.3986 6.35926 12.2857 6.64342 12.2857ZM6.64342 17.6429H17.3577C17.6419 17.6429 17.9144 17.7557 18.1153 17.9567C18.3162 18.1576 18.4291 18.4301 18.4291 18.7143C18.4291 18.9984 18.3162 19.271 18.1153 19.4719C17.9144 19.6728 17.6419 19.7857 17.3577 19.7857H6.64342C6.35926 19.7857 6.08673 19.6728 5.8858 19.4719C5.68487 19.271 5.57199 18.9984 5.57199 18.7143C5.57199 18.4301 5.68487 18.1576 5.8858 17.9567C6.08673 17.7557 6.35926 17.6429 6.64342 17.6429ZM6.64342 23H17.3577C17.6419 23 17.9144 23.1129 18.1153 23.3138C18.3162 23.5147 18.4291 23.7873 18.4291 24.0714C18.4291 24.3556 18.3162 24.6281 18.1153 24.829C17.9144 25.03 17.6419 25.1429 17.3577 25.1429H6.64342C6.35926 25.1429 6.08673 25.03 5.8858 24.829C5.68487 24.6281 5.57199 24.3556 5.57199 24.0714C5.57199 23.7873 5.68487 23.5147 5.8858 23.3138C6.08673 23.1129 6.35926 23 6.64342 23Z"
      fill="url(#paint0_linear_444_1681)"
    />
    <defs>
      <linearGradient
        id="paint0_linear_444_1681"
        x1="-2.94587"
        y1="0.553572"
        x2="26.947"
        y2="30.4464"
        gradientUnits="userSpaceOnUse"
      >
        <stop stop-color="#00D8FF" />
        <stop offset="1" stop-color="#0000FF" />
      </linearGradient>
    </defs>
  </svg>
);
